/* eslint-disable stylistic/comma-dangle */

/**
 * Header Theme Styles
 *
 * Uses variant system for consistent styling across header participants:
 * - variant="light": Black icons/text (for transparent, solid states)
 * - variant="dark": White icons/text (for gradient state)
 *
 * headerState determines background:
 * - transparent: No background
 * - gradient: Dark gradient background with box-shadow fade
 * - solid: Solid white background
 *
 * @see task-sticky-header.md for requirements
 */

import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';
import {A11Y_ICON_GLYPH_SIZE, A11Y_ICON_TARGET_MIN} from '@amblique/king-living/app/theme/foundations/a11y-targets';

/**
 * Color tokens for light variant (transparent, solid states)
 */
const lightColors = {
  text: 'grayAlpha.90',
  icon: 'grayAlpha.100',
  border: 'grayAlpha.100',
  locationText: 'primary.darkLinen',
};

/**
 * Color tokens for dark variant (gradient state)
 */
const darkColors = {
  text: 'text.white',
  icon: 'text.white',
  border: 'white',
  locationText: 'text.white',
};

/**
 * Get variant-specific color styles for all header parts
 * @param {Object} colors - Color tokens object
 * @returns {Object} Variant styles for header parts
 */
const getVariantStyles = (colors) => ({
  icons: {
    color: colors.icon,
  },
  searchIcon: {
    color: colors.icon,
  },
  nav: {
    color: colors.text,
  },
  border: {
    borderColor: colors.border,
  },
  buttonLocationText: {
    color: colors.locationText,
  },
});

export default {
  baseStyle: ({headerState = 'solid', isSticky = false}) => {
    const isTransparent = headerState === 'transparent';
    const isGradient = headerState === 'gradient';
    const isSolid = headerState === 'solid';

    // Dynamic background based on header state
    const getBackground = () => {
      if (isTransparent) return 'transparent';
      // Gradient state: transparent background, gradient is handled by ::after pseudo-element
      if (isGradient) return 'transparent';

      return 'background.primary'; // solid
    };

    // Dynamic box shadow
    const getBoxShadow = () => {
      if (isSolid && isSticky) return '0 2px 8px rgba(0,0,0,0.1)';
      if (isSolid) return 'base';

      return 'none';
    };

    // Dynamic bottom border color for mobile
    const getBorderColor = () => {
      if (isGradient) return 'whiteAlpha.450';

      return 'brandColors.greyscale20';
    };

    return {
      container: {
        minWidth: 'xs',
        width: 'full',
        // boxShadow: getBoxShadow(),
        background: getBackground(),
        transition: 'all 0.3s ease',
        position: 'relative',
        borderBottom: {base: '1px solid', lg: 'none'},
        borderColor: getBorderColor(),
        // Gradient overlay extending below header using ::after pseudo-element
        ...(isGradient && {
          zIndex: 10, // Header above page content
          overflow: 'visible', // Allow gradient to extend beyond header
          _after: {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: {base: 'calc(100% + 80px)', lg: 'calc(100% + 60px)'},
            background: {base: 'linear-gradient(180deg, rgba(0, 0, 0, 0.46) 34.08%, rgba(0, 0, 0, 0.00) 100%)', lg: 'linear-gradient(180deg, rgba(0, 0, 0, 0.46) 76.85%, rgba(0, 0, 0, 0) 100%)'},
            pointerEvents: 'none', // Allow clicks to pass through
            zIndex: -1, // Behind header content
          },
        }),
      },
      content: {
        maxWidth: '1440px',
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingX: {base: '20px', lg: '80px'},
        paddingTop: {base: '20px', lg: '40px'},
        paddingBottom: {base: '20px', lg: '0'},
        display: 'flex',
        flexDirection: 'column',
        rowGap: {base: 0, lg: '26px'},
        position: 'relative',
        zIndex: 1, // Above gradient overlay
      },
      searchContainer: {
        order: [2, 2, 2, 'inherit'],
        width: ['full', 'full', 'full', 60],
        marginRight: [0, 0, 0, 4],
        marginBottom: [1, 1, 2, 0],
      },
      bodyContainer: {
        flex: '1',
      },
      logo: {
        width: '100%',
        height: 'auto',
        ...(isGradient && {color: 'text.white'}),
      },
      iconLogo: {
        display: 'flex',
        marginLeft: {base: '9px', lg: 0},
        maxWidth: {base: '76px', lg: '112px'},
        maxHeight: {base: '17px', lg: '24px'},
        minWidth: 'auto',
        padding: 0,
        ...A11Y_FOCUS_VISIBLE,
      },
      icons: {
        alignItems: 'center',
        display: 'flex',
        height: A11Y_ICON_TARGET_MIN,
        justifyContent: 'center',
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        width: A11Y_ICON_TARGET_MIN,
        transition: 'color 0.3s ease',
        ...A11Y_FOCUS_VISIBLE,
        // Gradient state: white icons for contrast on dark overlay
        ...(isGradient && {color: 'text.white'}),
        sx: {
          svg: {
            maxWidth: A11Y_ICON_GLYPH_SIZE,
            maxHeight: A11Y_ICON_GLYPH_SIZE,
            ...(isGradient && {color: 'text.white'}),
          }
        }
      },
      cartIcon: {
        overflow: 'visible',
        position: 'relative',
      },
      searchIcon: {
        boxSize: A11Y_ICON_GLYPH_SIZE,
        flexShrink: 0,
        display: 'block',
        ...(isGradient && {color: 'text.white'}),
      },
      searchIconButton: {
        alignItems: 'center',
        display: 'flex',
        height: A11Y_ICON_TARGET_MIN,
        justifyContent: 'center',
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        p: 0,
        width: A11Y_ICON_TARGET_MIN,
        ...A11Y_FOCUS_VISIBLE,
        ...(isGradient && {color: 'text.white'}),
        sx: {
          '.chakra-icon': {
            height: A11Y_ICON_GLYPH_SIZE,
            width: A11Y_ICON_GLYPH_SIZE,
            ...(isGradient && {color: 'text.white'}),
          },
        },
      },
      cartBadge: {
        alignItems: 'center',
        backgroundColor: '#1B1A18',
        border: 'none',
        borderRadius: 'full',
        bottom: '-4px',
        boxSizing: 'border-box',
        color: 'white',
        display: 'inline-flex',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '11px',
        fontWeight: 500,
        height: '14px',
        justifyContent: 'center',
        lineHeight: 1.2,
        minWidth: '14px',
        paddingX: '2px',
        paddingY: 0,
        position: 'absolute',
        right: '-4px',
        textTransform: 'none',
        top: 'auto',
        left: 'auto',
      },
      // Gradient overlay (only visible in gradient state)
      gradientOverlay: {
        display: isGradient ? 'block' : 'none',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '201px',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 100%)',
        pointerEvents: 'none',
        zIndex: -1,
      },
      // Navigation text styles
      nav: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: 1.2,
        transition: 'color 0.3s ease',
        ...(isGradient && {color: 'text.white'}),
      },
      // Border styles (for dividers)
      border: {
        transition: 'border-color 0.3s ease',
        ...(isGradient && {borderColor: 'white'}),
      },
      buttonLocationIcon: {
        display: 'flex',
        justifyContent: 'center',
        height: 'auto',
        minWidth: 'auto',
        paddingX: {base: 0, md: 0, lg: 0},
        ...A11Y_FOCUS_VISIBLE,
      },
      buttonLocationText: {
        fontSize: '14px',
        fontWeight: 'normal',
        lineHeight: '120%',
        textDecoration: 'underline',
        textDecorationThickness: '1.4px',
        textUnderlineOffset: '2.8px',
        whiteSpace: 'nowrap',
        transition: 'color 0.3s ease',
        ...(isGradient && {color: 'text.white'}),
      },
      iconHamburger: {
        display: {base: 'flex', lg: 'none'},
      },
      actionsContainer: {
        display: 'flex',
        alignItems: 'center',
        columnGap: {base: '19px', lg: '32px'},
        sx: {
          '> *:empty': {
            display: 'none',
          },
        }
      },
      accountIcon: {
        display: {base: 'none', lg: 'inline-flex'},
        height: A11Y_ICON_TARGET_MIN,
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        width: A11Y_ICON_TARGET_MIN,
        cursor: 'pointer',
        alignSelf: ['self-start', 'self-start', 'self-start', 'auto'],
        ...A11Y_FOCUS_VISIBLE,
      },
      accountIconDesktop: {
        display: {base: 'none', lg: 'inline'},
        height: A11Y_ICON_TARGET_MIN,
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        width: A11Y_ICON_TARGET_MIN,
        cursor: 'pointer',
        alignSelf: ['self-start', 'self-start', 'self-start', 'auto'],
        ...A11Y_FOCUS_VISIBLE,
      },
      accountMenuPopover: {
        boxShadow: 'none',
      },
      popoverContent: {
        backgroundColor: 'white',
        border: '1px solid',
        borderColor: 'brandColors.greyscale20',
        borderRadius: '4px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
        zIndex: 1500,
        p: 0,
        overflow: 'hidden',
      },
      accountNameBadge: {
        backgroundColor: 'whiteAlpha.800',
        borderRadius: '50px',
        color: 'brandColors.greyscale80',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: A11Y_ICON_TARGET_MIN,
        height: A11Y_ICON_TARGET_MIN,
        fontSize: '11px',
        fontWeight: 500,
        minWidth: A11Y_ICON_TARGET_MIN,
        minHeight: A11Y_ICON_TARGET_MIN,
        lineHeight: '120%',
        ...A11Y_FOCUS_VISIBLE,
      },
      accountPopoverUserBlock: {
        px: 4,
        pt: 4,
        pb: 3,
      },
      accountPopoverAccountLink: {
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        borderRadius: 0,
        color: 'brandColors.dark',
        display: 'flex',
        justifyContent: 'flex-start',
        fontSize: '16px',
        fontWeight: '600',
        gap: 4,
        minHeight: '66px',
        textAlign: 'left',
        textDecoration: 'none',
        width: '100%',
        ...A11Y_FOCUS_VISIBLE,
        sx: {
          '&': {
            padding: '16px',
            textDecoration: 'none',
          },
          '&:hover, &:focus-visible': {
            textDecoration: 'none',
          }
        },
      },
      accountPopoverContentBody: {
        boxShadow: 'none',
        padding: '8px 0',
      },
      accountPopoverText: {
        color: 'brandColors.dark',
        fontSize: '16px',
        fontWeight: '600',
      },
      accountPopoverDivider: {
        borderColor: '#E8E8E8',
      },
      accountPopoverEmailText: {
        color: 'greyscale.70',
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '120%',
      },
      arrowDown: {
        height: A11Y_ICON_TARGET_MIN,
        minHeight: A11Y_ICON_TARGET_MIN,
        minWidth: A11Y_ICON_TARGET_MIN,
        width: A11Y_ICON_TARGET_MIN,
        marginRight: 0,
        alignSelf: ['self-start', 'self-start', 'self-start', 'auto'],
        cursor: 'pointer',
        _focus: {
          boxShadow: 'outline',
        },
        _focusVisible: {
          outline: 0,
        },
        display: ['none', 'none', 'none', 'block'],
      },
      wishlistIconDesktop: {
        display: {base: 'none', lg: 'flex'},
      },
      signout: {
        width: '100%',
        borderRadius: '4px',
        _hover: {
          background: 'gray.50',
        },
      },
      accountPopoverAccountIcon: {
        color: 'brandColors.primaryDarkLinen',
      },
      languageIconDesktop: {
        display: {base: 'none', lg: 'inline-flex'},
        sx: {
          '.chakra-icon': {
            width: '24px',
            height: '24px',
          }
        }
      },
    };
  },

  /**
   * Variants for header color scheme
   * - light: Black icons/text (for transparent, solid states)
   * - dark: White icons/text (for gradient state)
   */
  variants: {
    light: getVariantStyles(lightColors),
    dark: getVariantStyles(darkColors),
  },

  /**
   * Default variant (light = black text/icons)
   */
  defaultProps: {
    variant: 'light',
  },
  parts: [
    'container',
    'content',
    'actionsContainer',
    'searchContainer',
    'bodyContainer',
    'logo',
    'icons',
    'cartIcon',
    'cartBadge',
    'searchIcon',
    'searchIconButton',
    'signout',
    'signoutIcon',
    'signoutText',
    'languageIconDesktop',
    'wishlistIconDesktop',
    'iconHamburger',
    'buttonLocationIcon',
    'buttonLocationText',
    'gradientOverlay',
    'nav',
    'border',
    'accountIcon',
    'accountIconDesktop',
    'iconLogo',
    'popoverContent',
    'accountPopoverUserBlock',
    'accountPopoverAccountLink',
    'accountPopoverDivider',
    'accountPopoverEmailText',
    'arrowDown',
  ],
};
